
import React, { useEffect, useState } from 'react';
import { Layout, Typography, Button, Space, message, Row, Col, theme, Badge } from 'antd';
import {
    PlusOutlined,
    UploadOutlined,
    QuestionCircleOutlined,
    UsergroupAddOutlined,
    SearchOutlined,
    TeamOutlined,
    FolderOpenOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useGroupsStore from '../../stores/useGroupsStore';
import GroupList from './GroupList';
import TemplateInfoModal from './TemplateInfoModal';
import PageTourButton from '../guide/PageTourButton';
import { getGroupsSteps } from '../guide/toursteps';
import { withRouter } from 'react-router-dom';
import session from '../../utils/session';
import config from '../../config';
import './GroupsV2.css'; // We might need to create this or use inline/tailwind

const { Content } = Layout;
const { Title, Text } = Typography;

const GroupsV2 = ({ history }) => {
        const {
            groups,
            fetchGroups,
            templates,
            filters,
            setFilter,
            loading,
            setActiveCompanyId
        } = useGroupsStore();

    const [activeTemplate, setActiveTemplate] = useState(null);
    const { token } = theme.useToken();
    const primaryColor = '#4CAF50';

    // Derive the effective companyId: gh-admin uses the company selector (falling
    // back to session companyId when nothing is selected yet), others always use session.
    const reduxSelectedCompany = useSelector((state) => state.company?.selectedCompany);
    const userRole = session.get('userData')?.role || '';
    const isAdmin = userRole === config.userRoles.ghAdmin;
    const sessionCompanyId = session.get('userData')?.companyId;
    const companyId = (isAdmin && reduxSelectedCompany) ? reduxSelectedCompany : sessionCompanyId;

    // Sync the active company into the store and re-fetch when it changes
    useEffect(() => {
        setActiveCompanyId(companyId);
        if (companyId) {
            fetchGroups();
        }
    }, [companyId]);

    const handleCreateClick = () => {
        history.push('/group/create');
    };

    // Quick Action Chips
    const renderChips = () => (
        <div style={{ padding: '0px 24px 16px', background: 'white' }}>
            <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: '12px', textTransform: 'uppercase', color: '#888' }}>Common ways teams use groups</Text>
                <div style={{ fontSize: '13px', color: '#595959', marginTop: 4 }}>These are just ideas—your group is reusable for any campaign.</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {templates.filter(t => t.id !== 'custom').map(t => (
                    <Button
                        key={t.id}
                        shape="round"
                        onClick={() => setActiveTemplate(t)}
                        style={{ backgroundColor: '#fff', borderColor: '#d9d9d9', fontSize: '13px', display: 'flex', alignItems: 'center', height: 32, padding: '0 10px' }}
                    >
                        <PlusOutlined style={{ fontSize: '10px', marginRight: 2 }} />
                        <span style={{ marginRight: 2 }}>{t.icon}</span>
                        {t.name}
                    </Button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="gh-compact-page" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="gh-compact-panel" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <div
                                    className="tour-groups-create"
                                    style={{
                                        marginBottom: 16,
                                        background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                                        border: '1px solid #d9f7be',
                                        padding: 20
                                    }}
                                >
                                    <Row gutter={[16, 16]} align="middle" justify="space-between">
                                        <Col xs={24} md={16}>
                                            <Space direction="vertical" size={6}>
                                                <Title level={3} style={{ margin: 0, color: '#1f2937' }}>Groups <PageTourButton steps={getGroupsSteps()} /></Title>
                                                <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                                    Organize contacts into segments for targeted messaging, automated flows, and streamlined team management.
                                                </Text>
                                                <Space size={16}>
                                                    <Space size={6}>
                                                        <TeamOutlined style={{ color: '#08979c' }} />
                                                        <Text type="secondary">{groups.length} total groups</Text>
                                                    </Space>
                                                    <Space size={6}>
                                                        <FolderOpenOutlined style={{ color: '#08979c' }} />
                                                        <Text type="secondary">Dynamic segmenting</Text>
                                                    </Space>
                                                </Space>
                                            </Space>
                                        </Col>
                                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                                            <Space wrap>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={handleCreateClick}
                                                    style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                                                >
                                                    Create Group
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>
                {/* Main Content Area */}
                <div style={{ flex: 1, margin: '0', backgroundColor: 'white', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <>
                        {renderChips()}
                        <Content style={{ padding: '0 20px 20px', overflowY: 'auto' }}>
                            <GroupList />
                        </Content>
                    </>
                </div>
            </div>
            <TemplateInfoModal
                template={activeTemplate}
                open={!!activeTemplate}
                onClose={() => setActiveTemplate(null)}
            />
        </div>
    );
};

export default withRouter(GroupsV2);
